// src/pages/PrintOffsPage.js — cleaned, fixed imports, correct buttons, usage tracking added
import React, { useState, useRef } from 'react';
import '../printStyles.css';
import { useAuth } from '../contexts/AuthContext';
import { incrementDocUsage } from '../utils/usage';
import { exportToWord } from '../utils/exportToWord';

// === Forms (ensure these files exist in src/printoffs/ or adjust paths accordingly) ===
import AccidentForm from './AccidentForm';
import DriverAndVehicleLog from './DriverAndVehicleLog';
import DriverAssessment from './DriverAssessment';
import DriverDailyWalkaroundCheckLog from './DriverDailyWalkaroundCheckLog';
import DriverTrainingLog from './DriverTrainingLog';
import DriverHandbookLog from './DriverHandbookLog';
import DVLACheck from './DVLACheck';
import DrugTestLog from './DrugTestLog';
import FinesInfringementsLog from './FinesInfringementsLog';
import FireSafetyCheck from './FireSafetyCheck';
import FitToDrive from './FitToDrive';
import GateCheck from './GateCheck';
import InvoiceTemplate from './InvoiceTemplate';
import LicenceAndDocumentsValidityCheck from './LicenceAndDocumentsValidityCheck';
import MaintenanceSchedule from './MaintenanceSchedule';
import ReportIt from './ReportIt';
import TimeSheet from './TimeSheet';
import TyreTorqueCheckForm from './TyreTorqueCheckForm';
import VehicleComplianceForm from './VehicleComplianceForm';
import VehicleDefectReportForm from './VehicleDefectReportForm';
import VehicleWalkaroundCheck from './VehicleWalkaroundCheck';
import VORForm from './VORForm';
import VORSign from './VORSign';
import WalkaroundGuide from './WalkaroundGuide';

// Small helper: nice labels for export file names
const TITLES = {
  AccidentForm: 'Accident Form',
  DriverAndVehicleLog: 'Driver and Vehicle Log',
  DriverAssessment: 'Driver Assessment',
  DriverDailyWalkaroundCheckLog: 'Driver Daily Walkaround Check Log',
  DriverTrainingLog: 'Driver Training Log',
  DriverHandbookLog: 'Driver Handbook Log',
  DVLACheck: 'DVLA Check',
  DrugTestLog: 'Drug Test Log',
  FinesInfringementsLog: 'Fines/Infringements Log',
  FireSafetyCheck: 'Fire Safety Check',
  FitToDrive: 'Fit To Drive',
  GateCheck: 'Gate Check',
  InvoiceTemplate: 'Invoice Template',
  LicenceAndDocumentsValidityCheck: 'Licence & Documents Validity Check',
  MaintenanceSchedule: 'Maintenance Schedule',
  ReportIt: 'Report It',
  TimeSheet: 'Time Sheet',
  TyreTorqueCheckForm: 'Tyre & Wheel Torque Check Form',
  VehicleComplianceForm: 'Vehicle Compliance Form',
  VehicleDefectReportForm: 'Vehicle Defect Report Form',
  VehicleWalkaroundCheck: 'Vehicle Walkaround Check',
  VORForm: 'VOR Form',
  VORSign: 'VOR Sign',
  WalkaroundGuide: 'Walkaround Guide',
};

function PrintOffsPage() {
  const { user } = useAuth();
  const [selectedForm, setSelectedForm] = useState('');
  const formRef = useRef(null);

  // When a button is clicked: select the form and record usage
  const handleFormSelection = async (formKey) => {
    setSelectedForm(formKey);
    try {
      if (user?.uid) {
        await incrementDocUsage(user.uid, formKey, TITLES[formKey] || formKey, 'printoff');
      }
    } catch (_) {}
  };

  const handleExportToWord = () => {
    if (formRef.current) {
      const safeName = (TITLES[selectedForm] || selectedForm || 'printoff').replace(/\s+/g, '_');
      exportToWord(formRef.current.innerHTML, `${safeName}.docx`);
    } else {
      alert('No form selected to export.');
    }
  };

  // Render the selected form inside a wrapper we can export
  const renderSelectedForm = () => {
    switch (selectedForm) {
      case 'AccidentForm':
        return <AccidentForm />;
      case 'DriverAndVehicleLog':
        return <DriverAndVehicleLog />;
      case 'DriverAssessment':
        return <DriverAssessment />;
      case 'DriverDailyWalkaroundCheckLog':
        return <DriverDailyWalkaroundCheckLog />;
      case 'DriverTrainingLog':
        return <DriverTrainingLog />;
      case 'DriverHandbookLog':
        return <DriverHandbookLog />;
      case 'DVLACheck':
        return <DVLACheck />;
      case 'DrugTestLog':
        return <DrugTestLog />;
      case 'FinesInfringementsLog':
        return <FinesInfringementsLog />;
      case 'FireSafetyCheck':
        return <FireSafetyCheck />;
      case 'FitToDrive':
        return <FitToDrive />;
      case 'GateCheck':
        return <GateCheck />;
      case 'InvoiceTemplate':
        return <InvoiceTemplate />;
      case 'LicenceAndDocumentsValidityCheck':
        return <LicenceAndDocumentsValidityCheck />;
      case 'MaintenanceSchedule':
        return <MaintenanceSchedule />;
      case 'ReportIt':
        return <ReportIt />;
      case 'TimeSheet':
        return <TimeSheet />;
      case 'TyreTorqueCheckForm':
        return <TyreTorqueCheckForm />;
      case 'VehicleComplianceForm':
        return <VehicleComplianceForm />;
      case 'VehicleDefectReportForm':
        return <VehicleDefectReportForm />;
      case 'VehicleWalkaroundCheck':
        return <VehicleWalkaroundCheck />;
      case 'VORForm':
        return <VORForm />;
      case 'VORSign':
        return <VORSign />;
      case 'WalkaroundGuide':
        return <WalkaroundGuide />;
      default:
        return null;
    }
  };

  return (
    <div className="p-4 bg-gray-100 min-h-screen">
      <h2 className="text-3xl font-semibold text-center text-gray-800 mb-8">Print‑Offs</h2>
      <p className="p-8 text-lg text-center">
        Select a form below. Then scroll to the bottom to view, edit, print, or export.
      </p>

      {/* Buttons (alphabetical) */}
      <div className="flex flex-col items-center space-y-4 max-w-lg mx-auto">
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('AccidentForm')}>Accident Form</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('DriverAndVehicleLog')}>Driver and Vehicle Log</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('DriverAssessment')}>Driver Assessment</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('DriverDailyWalkaroundCheckLog')}>Driver Daily Walkaround Check Log</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('DriverHandbookLog')}>Driver Handbook Log</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('DriverTrainingLog')}>Driver Training Log</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('DVLACheck')}>DVLA Check</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('DrugTestLog')}>Drug Test Log</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('FinesInfringementsLog')}>Fines/Infringements Log</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('FireSafetyCheck')}>Fire Safety Check</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('FitToDrive')}>Fit To Drive</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('GateCheck')}>Gate Check</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('InvoiceTemplate')}>Invoice Template</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('LicenceAndDocumentsValidityCheck')}>Licence and Documents Validity Check</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('MaintenanceSchedule')}>Maintenance Schedule</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('ReportIt')}>Report It</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('TimeSheet')}>Time Sheet</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('TyreTorqueCheckForm')}>Tyre & Wheel Torque Check Form</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('VehicleComplianceForm')}>Vehicle Compliance Form</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('VehicleDefectReportForm')}>Vehicle Defect Report Form</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('VehicleWalkaroundCheck')}>Vehicle Walkaround Check</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('VORForm')}>VOR Form</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('VORSign')}>VOR Sign</button>
        <button className="w-full bg-blue-500 text-white p-4 rounded-lg hover:bg-blue-600 transition-colors shadow-lg" onClick={() => handleFormSelection('WalkaroundGuide')}>Walkaround Guide</button>
      </div>

      {/* Export to Word */}
      {selectedForm && (
        <div className="mt-6 text-center">
          <button
            onClick={handleExportToWord}
            className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition mb-4"
          >
            Export to Word
          </button>
        </div>
      )}

      {/* Selected form content */}
      <div className="mt-6 p-4 bg-white rounded-lg shadow-lg" ref={formRef}>
        {renderSelectedForm()}
      </div>
    </div>
  );
}

export default PrintOffsPage;
